/**
 * A one-event pub/sub for the raw `extract.done` Socket.io payload.
 *
 * ---- Why this exists instead of a second `useFrappeEventListener('extract.done', ...)` ----
 *
 * `useRealtime.ts`'s header is explicit: it is mounted exactly ONCE, in `Layout.tsx`, and
 * "nothing else calls `useFrappeEventListener` — a second call site would double-subscribe."
 * That is not just tidiness. `frappe-react-sdk`'s implementation subscribes with
 * `socket.on(eventName, callback)` but unsubscribes on unmount with `socket.off(eventName)` —
 * NO callback reference. Socket.io's `off(eventName)` with no handler removes EVERY listener
 * registered for that event name on the shared socket, not just the caller's own. A second,
 * independent `useFrappeEventListener('extract.done', ...)` call site (e.g. mounted only while
 * `CreateEnquiryBar` is open) would, on its own unmount, silently kill `useRealtime`'s central
 * listener for the rest of the session — no error, no warning, just `mail.new`/`sq.received`/
 * `enquiry.status` invalidation quietly stopping.
 *
 * So there is still exactly one subscription: the one inside `useRealtime`. This module is how
 * that single subscription hands the payload to a SECOND, independent CONSUMER —
 * `useMailExtraction.ts` — without a second `.on()`/`.off()` pair anywhere. `useRealtime.ts`
 * calls `publishExtractDone(payload)` for every `extract.done` it receives, in addition to (not
 * instead of) its own payload-free invalidation. See that file's header for the fuller context.
 */

type ExtractDoneListener = (payload: unknown) => void

const listeners = new Set<ExtractDoneListener>()

/** Called once, from `useRealtime.ts`, for every `extract.done` event the socket delivers. */
export function publishExtractDone(payload: unknown): void {
  for (const listener of listeners) listener(payload)
}

/** Subscribe to every future `extract.done` payload. Returns an unsubscribe function. */
export function subscribeExtractDone(listener: ExtractDoneListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
